import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import MobileWorkspaceCard from './MobileWorkspaceCard';

interface Workspace {
  id: string;
  name: string;
  description?: string;
  owner_org_id: string;
  owner_org_name: string;
  owner_org_subscription?: string;
  has_subscription?: boolean;
  created_at: string;
  updated_at: string;
  bill_count?: number;
  can_manage?: boolean;
  logo_file?: string;
  logo_url?: string;
  unread_notes_count?: number;
}

interface Organization {
  id: string;
  role?: string;
}

interface MobileOrganizationWorkspaceListProps {
  workspaces: Workspace[];
  organizations: Organization[];
  onWorkspaceClick: (workspace: Workspace) => void;
}

const MobileOrganizationWorkspaceList: React.FC<MobileOrganizationWorkspaceListProps> = ({
  workspaces,
  organizations,
  onWorkspaceClick
}) => {
  // Group workspaces by organization
  const workspacesByOrg = workspaces.reduce((acc, workspace) => {
    const orgId = workspace.owner_org_id;
    if (!acc[orgId]) {
      acc[orgId] = {
        orgId,
        orgName: workspace.owner_org_name,
        workspaces: []
      };
    }
    acc[orgId].workspaces.push(workspace);
    return acc;
  }, {} as Record<string, { orgId: string; orgName: string; workspaces: Workspace[] }>);

  const orgGroups = Object.values(workspacesByOrg);
  const hasMultipleOrgs = orgGroups.length > 1;

  // Track which organizations are expanded (all expanded by default)
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(
    new Set(orgGroups.map(g => g.orgId))
  );

  const toggleOrg = (orgId: string) => {
    setExpandedOrgs(prev => {
      const next = new Set(prev);
      if (next.has(orgId)) {
        next.delete(orgId);
      } else {
        next.add(orgId);
      }
      return next;
    });
  };

  // If only 1 organization, show flat workspace list
  if (!hasMultipleOrgs && orgGroups.length === 1) {
    return (
      <div className="space-y-3">
        {orgGroups[0].workspaces.map(workspace => (
          <MobileWorkspaceCard
            key={workspace.id}
            workspace={workspace}
            onWorkspaceClick={onWorkspaceClick}
            organizations={organizations}
          />
        ))}
      </div>
    );
  }

  // Multiple organizations - show hierarchical view
  return (
    <div className="space-y-4">
      {orgGroups.map(group => {
        const isExpanded = expandedOrgs.has(group.orgId);
        
        return (
          <div key={group.orgId} className="space-y-2">
            {/* Organization Header */}
            <button
              onClick={() => toggleOrg(group.orgId)}
              className="w-full card p-3 flex items-center gap-3 active:scale-[0.98] transition-all duration-150"
            >
              {/* Icon */}
              <div className="w-10 h-10 rounded bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>

              {/* Organization Name */}
              <div className="flex-1 min-w-0 text-left">
                <div className="font-semibold text-base text-text-primary dark:text-text-dark-primary truncate">
                  {group.orgName}
                </div>
                <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {group.workspaces.length} {group.workspaces.length === 1 ? 'workspace' : 'workspaces'}
                </div>
              </div>

              {/* Expand/Collapse Icon */}
              <div className="flex-shrink-0">
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
                )}
              </div>
            </button>

            {/* Workspaces List (when expanded) */}
            {isExpanded && (
              <div className="space-y-3 pl-2">
                {group.workspaces.map(workspace => (
                  <MobileWorkspaceCard
                    key={workspace.id}
                    workspace={workspace}
                    onWorkspaceClick={onWorkspaceClick}
                    organizations={organizations}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MobileOrganizationWorkspaceList;
